import { useLingui } from "@lingui/react/macro";
import { Button, LinkButton, type ServerError } from "@offstreetparking/design-system/forms";
import { AddIcon } from "@offstreetparking/design-system/icons/AddIcon";
import { uniqueId } from "@offstreetparking/design-system/utils";
import { RFC_5322_EMAIL_REGEX } from "@offstreetparking/domain/value/email";
import { useId, useRef, useState } from "react";
import { useParker } from "../../../providers/parker";
import { useZod } from "../../providers/TranslationsProvider";
import { Form } from "../Form";
import { EmailInput, EmailValidationModal, suggestEmail } from "./";

type SendEmailFormProps = {
	onSubmit: (emails: string[]) => void;
	isLoading: boolean;
	serverError?: ServerError;
	companyId: number;
};

export const SendEmailForm = ({ onSubmit, isLoading, serverError, companyId }: SendEmailFormProps) => {
	const { t } = useLingui();
	const z = useZod();
	const { parker } = useParker();
	const [validationModalState, setValidationModalState] = useState<{
		suggested: string;
		original: string;
	}>();
	const skipEmailSuggestion = useRef([false]);
	const formId = useId();

	return (
		<Form
			key={parker?.email}
			serverError={serverError}
			id={formId}
			schema={z.object({
				emails: z
					.object({
						id: z.string(),
						email: z.email({
							pattern: RFC_5322_EMAIL_REGEX,
						}),
					})
					.array(),
			})}
			initialValues={{
				emails: [
					{
						id: uniqueId(),
						email: parker?.email ?? "",
					},
				],
			}}
			onSubmit={({ emails }) => {
				let requiresCorrection = false;
				emails.forEach(({ email }, index) => {
					const { hasCorrection, suggested } = suggestEmail((email ?? "").toLocaleLowerCase());
					if (email && hasCorrection && !skipEmailSuggestion.current[index]) {
						setValidationModalState({
							suggested: suggested!,
							original: email,
						});
						requiresCorrection = true;
					}
				});
				if (!requiresCorrection) {
					onSubmit(emails.map(({ email }) => email.toLocaleLowerCase()));
				}
			}}
		>
			{({ setValue, watch, clearErrors, formState: { errors } }) => {
				const emails = watch("emails");
				return (
					<>
						{emails.length > 0 && (
							<div className="mb-2 space-y-4">
								{emails.map(({ id, email }, index) => (
									<EmailInput
										companyId={companyId}
										key={id}
										error={errors.emails?.[index]?.email?.message}
										name={`emails.${index}.email`}
										label={t`Email`}
										onChange={(nextEmail) => {
											skipEmailSuggestion.current[index] = false;
											clearErrors(`emails.${index}.email`);
											setValue(`emails.${index}.email`, nextEmail);
										}}
										onClear={
											index > 0
												? () => {
														setValue(
															"emails",
															emails.filter((_, i) => i !== index),
														);
													}
												: undefined
										}
										defaultValue={email}
										placeholder="john_doe@gmail.com"
										isRequired
									/>
								))}
							</div>
						)}

						<EmailValidationModal
							key={validationModalState?.suggested}
							suggested={validationModalState?.suggested}
							original={validationModalState?.original}
							onContinue={(updatedEmail) => {
								const updatedEmails = emails.map((item, idx) => {
									if (item.email === validationModalState?.original) {
										skipEmailSuggestion.current[idx] = true;
										return {
											...item,
											email: updatedEmail,
										};
									}
									return item;
								});
								setValidationModalState(undefined);
								setValue("emails", updatedEmails);
								(document.getElementById(formId) as HTMLFormElement).requestSubmit();
							}}
							onClose={() => setValidationModalState(undefined)}
						/>
						{emails.length < 3 && (
							<LinkButton
								type="button"
								size="sm"
								leftIcon={AddIcon}
								onClick={() => {
									skipEmailSuggestion.current.push(false);
									setValue(
										`emails.${emails.length}`,
										{
											id: uniqueId(),
											email: "",
										},
										{
											shouldValidate: false,
										},
									);
								}}
							>
								{t`Add another email`}
							</LinkButton>
						)}
						{emails.length > 0 && (
							<Button
								size="md"
								isLoading={isLoading}
								className="mt-4 w-full p-4 font-bold"
								intent="primary"
							>
								{t`Send`}
							</Button>
						)}
					</>
				);
			}}
		</Form>
	);
};
